/**
 * Editor для doc.payment_orders (Фаза 2.1 #2).
 * State-aware UI: поля editable тільки у DRAFT.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  paymentOrders,
  type PaymentDirection,
  type PaymentOrderState,
} from '@/api/paymentOrders';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';

const PERMISSION = 'documents.payment_orders.create';

interface FormState {
  organization_id: string;
  document_date: string;
  direction: PaymentDirection;
  contractor_id: string;
  bank_account_id: string;
  currency_id: string;
  amount_currency: string;
  amount_uah: string;
  payment_purpose: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_date: new Date().toISOString().slice(0, 16),
  direction: 'OUT',
  contractor_id: '',
  bank_account_id: '',
  currency_id: '',
  amount_currency: '0',
  amount_uah: '0',
  payment_purpose: '',
  comment: '',
};

const STATE_LABEL: Record<PaymentOrderState, string> = {
  DRAFT: 'Чернетка',
  SENT_TO_BANK: 'Відправлено у банк',
  EXECUTED: 'Виконано',
  CANCELLED: 'Скасовано',
};

export default function PaymentOrderEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const docQuery = useQuery({
    queryKey: ['payment-order', id],
    queryFn: () => paymentOrders.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_date: d.document_date.slice(0, 16),
        direction: d.direction,
        contractor_id: d.contractor_id ?? '',
        bank_account_id: d.bank_account_id,
        currency_id: d.currency_id,
        amount_currency: d.amount_currency,
        amount_uah: d.amount_uah,
        payment_purpose: d.payment_purpose,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const state = docQuery.data?.state ?? 'DRAFT';
  const isDraft = state === 'DRAFT';
  const isPosted = docQuery.data?.posted ?? false;

  const create = useMutation({
    mutationFn: () =>
      paymentOrders.create({
        organization_id: form.organization_id,
        document_date: new Date(form.document_date).toISOString(),
        direction: form.direction,
        contractor_id: form.contractor_id || null,
        bank_account_id: form.bank_account_id,
        currency_id: form.currency_id,
        amount_currency: form.amount_currency,
        amount_uah: form.amount_uah,
        payment_purpose: form.payment_purpose,
        comment: form.comment || null,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['payment-orders'] });
      navigate(`/banking/payment-orders/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const sendToBank = useMutation({
    mutationFn: () => paymentOrders.sendToBank(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-order', id] }),
    onError: (e) => setError(extractApiError(e)),
  });
  const execute = useMutation({
    mutationFn: () => paymentOrders.execute(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-order', id] }),
    onError: (e) => setError(extractApiError(e)),
  });
  const cancel = useMutation({
    mutationFn: () => paymentOrders.cancel(id!, cancelReason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-order', id] }),
    onError: (e) => setError(extractApiError(e)),
  });
  const postDoc = useMutation({
    mutationFn: () => paymentOrders.post(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-order', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        bank_account_id: form.bank_account_id,
        currency_id: form.currency_id,
        payment_purpose: form.payment_purpose,
      },
      {
        organization_id: 'Юрособа',
        bank_account_id: 'Банк-рахунок',
        currency_id: 'Валюта',
        payment_purpose: 'Призначення платежу',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий платіжний ордер' : `Платіжний ордер ${docQuery.data?.document_number}`}
          {!isNew && (
            <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
              {STATE_LABEL[state]}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/banking/payment-orders')}>Назад</button>
          {isNew && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={handleSave} disabled={create.isPending}>
                Зберегти
              </button>
            </RequirePermission>
          )}
          {!isNew && state === 'DRAFT' && (
            <button onClick={() => sendToBank.mutate()}>Відправити у банк</button>
          )}
          {!isNew && state === 'SENT_TO_BANK' && (
            <button onClick={() => execute.mutate()}>Виконано банком</button>
          )}
          {!isNew && state === 'EXECUTED' && !isPosted && (
            <button className="primary" onClick={() => postDoc.mutate()}>Провести</button>
          )}
          {!isNew && (state === 'DRAFT' || state === 'SENT_TO_BANK') && (
            <button
              className="danger"
              onClick={() => {
                const reason = window.prompt('Причина скасування:');
                if (reason) { setCancelReason(reason); cancel.mutate(); }
              }}
            >
              Скасувати
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Юрособа</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
              ))}
            </select>
          ) : (
            <div>{orgList.data?.items.find((o) => o.id === form.organization_id)?.name ?? form.organization_id}</div>
          )}
        </div>
        <div className="field">
          <label>Дата документа</label>
          <DatePopover
            withTime
            value={form.document_date}
            disabled={!isDraft && !isNew}
            onChange={(v) => setForm({ ...form, document_date: v })}
          />
        </div>
        <div className="field">
          <RequiredLabel>Напрямок</RequiredLabel>
          <select
            value={form.direction}
            disabled={!isNew}
            onChange={(e) => setForm({ ...form, direction: e.target.value as PaymentDirection })}
          >
            <option value="OUT">↑ Списання (OUT)</option>
            <option value="IN">↓ Надходження (IN)</option>
          </select>
        </div>
        <div className="field">
          <label>Контрагент (UUID, опц.)</label>
          <input
            value={form.contractor_id}
            disabled={!isDraft && !isNew}
            onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}
          />
        </div>
        <div className="field">
          <RequiredLabel>Банк-рахунок (UUID)</RequiredLabel>
          <input
            value={form.bank_account_id}
            disabled={!isDraft && !isNew}
            onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
          />
        </div>
        <div className="field">
          <RequiredLabel>Валюта (UUID)</RequiredLabel>
          <input
            value={form.currency_id}
            disabled={!isDraft && !isNew}
            onChange={(e) => setForm({ ...form, currency_id: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Сума у валюті</label>
          <input
            type="number" step="0.0001"
            value={form.amount_currency}
            disabled={!isDraft && !isNew}
            onChange={(e) => setForm({ ...form, amount_currency: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Сума UAH</label>
          <input
            type="number" step="0.0001"
            value={form.amount_uah}
            disabled={!isDraft && !isNew}
            onChange={(e) => setForm({ ...form, amount_uah: e.target.value })}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <RequiredLabel>Призначення платежу</RequiredLabel>
          <textarea
            value={form.payment_purpose}
            disabled={!isDraft && !isNew}
            onChange={(e) => setForm({ ...form, payment_purpose: e.target.value })}
            rows={2}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            disabled={!isDraft && !isNew}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
